import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { isEmpty } from 'lodash'
import {
  EduButton,
  EduIf,
  FlexContainer,
  PortalSpinner,
  SelectInputStyled,
  TextAreaInputStyled,
} from '@edulastic/common'
import PropTypes from 'prop-types'

import connect from 'react-redux/es/connect/connect'

import { IconGoogleTranslate } from '@edulastic/icons'
import { white } from '@edulastic/colors'
import { languageByCode } from '@edulastic/constants/const/test'
import { StyledSpeakableTextContainer } from './styled-components'

import { allowedToSelectMultiLanguageInTest } from '../../../../selectors/user'

import { changeTestDataToPreferredLanguage } from '../../../../../../assessment/utils/test'
import AudioControls from '../../../../../../assessment/AudioControls'
import { getAvailableLanguageKeysInTest } from '../../../../../../common/utils/helpers'
import { filterAndOrderByLanguageOptions } from '../../../../../questionUtils'

const TestSpeakableText = ({
  ttsTextAPIStatus,
  updateTTSAPIStatus,
  ttsTextData = {},
  updateTTSText,
  test,
  showInstructionAcknowledgement,
  regenerateTTSText,
  onLanguageChange,
  selectedLanguage,
  allowedToSelectMultiLanguage,
  isLoading,
}) => {
  const [updatedTtsData, setUpdatedTtsData] = useState(ttsTextData)

  useEffect(() => {
    setUpdatedTtsData(ttsTextData)
  }, [ttsTextData])

  const {
    text: instruction = '',
    instructionAcknowledgementText: {
      text: instructionAcknowledgementText = '',
    } = {},
  } = updatedTtsData || {}

  const handleUpdateTtsData = (text, type) => {
    if (type === 'instruction') {
      setUpdatedTtsData((previousState) => ({
        ...previousState,
        text,
      }))
    } else if (type === 'instructionAcknowledgementText') {
      setUpdatedTtsData((previousState) => ({
        ...previousState,
        instructionAcknowledgementText: { text },
      }))
    }
  }

  const handleUpdateTTSText = () => {
    updateTTSText({
      testId: test._id,
      data: updatedTtsData,
      language: selectedLanguage,
    })
  }

  if (isEmpty(test)) {
    return null
  }

  const showLanguageSelector =
    allowedToSelectMultiLanguage &&
    Object.keys(test?.languageFeatures || {}).length

  const testDataByLanguage = changeTestDataToPreferredLanguage(
    test,
    selectedLanguage
  )

  const audioSrc = testDataByLanguage?.testTTS?.instructionAudioURL

  return (
    <>
      <EduIf condition={showLanguageSelector}>
        <FlexContainer justifyContent="flex-end">
          <SelectInputStyled
            data-cy="tts-language-selector"
            width="120px"
            height="30px"
            onSelect={(value) => onLanguageChange(value)}
            value={selectedLanguage}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            bg={white}
            disabled={ttsTextAPIStatus === 'INITIATED' || isLoading}
          >
            {filterAndOrderByLanguageOptions(
              getAvailableLanguageKeysInTest(test)
            ).map((language) => (
              <option value={language} key={language}>
                <FlexContainer justifyContent="center" alignItems="center">
                  <IconGoogleTranslate margin="0 5px 0 0" />
                  {languageByCode[language]}
                </FlexContainer>
              </option>
            ))}
          </SelectInputStyled>
        </FlexContainer>
      </EduIf>
      <EduIf condition={ttsTextAPIStatus === 'INITIATED'}>
        <PortalSpinner />
      </EduIf>

      <StyledSpeakableTextContainer
        margin={showLanguageSelector ? '0px' : '10px 0 0 0'}
      >
        {audioSrc && (
          <AudioControls
            key={test._id}
            test={testDataByLanguage}
            audioSrc={audioSrc}
            className="speakable-text-audio-controls"
            isMinimalDesign
            isTestSettings
          />
        )}

        <StyledTTStextWrapper>
          <h4>Test Instruction</h4>
          <TextAreaInputStyled
            style={{
              paddingLeft: '5px',
              paddingTop: '5px',
              marginBottom: '10px',
            }}
            value={instruction}
            autoSize={{ minRows: 6, maxRows: 8 }}
            onChange={(e) =>
              handleUpdateTtsData(e?.target?.value || '', 'instruction')
            }
            disabled={ttsTextAPIStatus === 'INITIATED' || isLoading}
            data-cy="ttsInstructionText"
          />
          {showInstructionAcknowledgement && (
            <>
              <h4>Acknowledgement</h4>
              <TextAreaInputStyled
                style={{
                  paddingLeft: '5px',
                  paddingTop: '5px',
                  marginBottom: '10px',
                }}
                value={instructionAcknowledgementText}
                autoSize={{ minRows: 4, maxRows: 8 }}
                onChange={(e) =>
                  handleUpdateTtsData(
                    e?.target?.value || '',
                    'instructionAcknowledgementText'
                  )
                }
                disabled={ttsTextAPIStatus === 'INITIATED' || isLoading}
                data-cy="ttsInstructionAcknowledgementText"
              />
            </>
          )}
        </StyledTTStextWrapper>
        <FlexContainer justifyContent="space-between">
          <FlexContainer justifyContent="flex-end" alignItems="center">
            <EduButton
              isGhost
              loading={ttsTextAPIStatus === 'INITIATED' || isLoading}
              onClick={regenerateTTSText}
              disabled={ttsTextAPIStatus === 'INITIATED' || isLoading}
              data-cy="regenerateTTSTextButton"
            >
              Regenerate TTS Text
            </EduButton>
            <EduButton
              loading={updateTTSAPIStatus === 'INITIATED' || isLoading}
              onClick={handleUpdateTTSText}
              disabled={updateTTSAPIStatus === 'INITIATED' || isLoading}
              data-cy="generateTTSTextButton"
            >
              Generate TTS Audio
            </EduButton>
          </FlexContainer>
        </FlexContainer>
      </StyledSpeakableTextContainer>
    </>
  )
}

export default connect((state) => ({
  allowedToSelectMultiLanguage: allowedToSelectMultiLanguageInTest(state),
}))(TestSpeakableText)

TestSpeakableText.propTypes = {
  ttsTextAPIStatus: PropTypes.string,
  updateTTSAPIStatus: PropTypes.string,
  ttsTextData: PropTypes.object,
  updateTTSText: PropTypes.func,
  test: PropTypes.object,
  showInstructionAcknowledgement: PropTypes.bool,
}

TestSpeakableText.defaultProps = {
  ttsTextAPIStatus: 'INITIATED',
  updateTTSAPIStatus: 'INITIATED',
  ttsTextData: {},
  updateTTSText: () => {},
  test: {},
  showInstructionAcknowledgement: false,
}

const StyledTTStextWrapper = styled(FlexContainer)`
  overflow: auto;
  flex-direction: column;
  justify-content: flex-start;
`
